import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginAsTenant } from '../../api/tenants.api';
import { usePlatformPermissions } from '../../hooks/usePlatformPermissions';

type Props = {
  tenantId: string | number;
  variant?: 'link' | 'button' | 'icon';
  className?: string;
  label?: string;
};

export default function LoginAsTenantButton({
  tenantId,
  variant = 'link',
  className = '',
  label,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePlatformPermissions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!can('login_as_atelier')) {
    return null;
  }

  const text = label || t('actions.login_as', { defaultValue: 'الدخول كأتيليه' });

  const onClick = async () => {
    setBusy(true);
    setError('');
    const result = await loginAsTenant(tenantId);
    setBusy(false);
    if (result.ok === false) {
      if (result.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(result.message);
      return;
    }
    window.open(result.handoffUrl, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'icon') {
    return (
      <div className="relative inline-flex flex-col items-start">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onClick()}
          title={text}
          className={`w-8 h-8 rounded-lg text-teal-700 hover:bg-teal-50 disabled:opacity-40 cursor-pointer inline-flex items-center justify-center ${className}`}
        >
          <i className={`${busy ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'} text-base`} />
        </button>
        {error ? <span className="absolute top-full start-0 mt-1 text-[10px] text-rose-600 whitespace-nowrap z-10">{error}</span> : null}
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <div className="inline-flex flex-col items-start gap-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onClick()}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 cursor-pointer ${className}`}
        >
          <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'} />
          {text}
        </button>
        {error ? <span className="text-[11px] text-rose-600">{error}</span> : null}
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <button
        type="button"
        disabled={busy}
        onClick={() => void onClick()}
        className={`inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline disabled:opacity-40 cursor-pointer ${className}`}
      >
        <i className={busy ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'} />
        {busy ? t('ateliers.login_as_loading', { defaultValue: 'جارٍ الدخول…' }) : text}
      </button>
      {error ? <span className="text-[11px] text-rose-600 max-w-[180px]">{error}</span> : null}
    </div>
  );
}
